<template>
  <div>

    <!-- HERO -->
    <AboutHero :showCtas="true" />

    <!-- STATS — números da empresa, clean, sem cópia do KPI -->
    <section ref="statsRef" class="py-16 bg-white border-b border-gray-100">
      <div class="container-custom">
        <div class="grid grid-cols-2 lg:grid-cols-4 divide-y-2 lg:divide-y-0 lg:divide-x divide-gray-100">
          <div v-for="(stat, i) in animatedStats" :key="stat.id" :class="[
            'px-8 py-10 text-center',
            'transition-all duration-700 ease-out',
            statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          ]" :style="{ transitionDelay: (i * 80) + 'ms' }">
            <p class="text-5xl sm:text-6xl font-black leading-none text-gray-900 tabular-nums">
              {{ stat.prefix }}{{ formatNumber(stat.displayed) }}<span class="text-kali-blue-500">{{ stat.suffix
              }}</span>
            </p>
            <p class="mt-3 text-sm text-gray-500 font-medium leading-snug">{{ stat.label }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 01 — POR QUE EXISTIMOS -->
    <section ref="whyRef" class="py-28 bg-gray-50 overflow-hidden">
      <div class="container-custom">
        <div class="flex items-end gap-5 mb-16">
          <span class="text-[6rem] font-black text-gray-200 leading-none select-none" aria-hidden="true">01</span>
          <div class="pb-3">
            <p class="text-xs font-bold text-kali-blue-500 uppercase tracking-widest mb-1">Nossa razão de existir</p>
            <h2 class="text-3xl font-black text-gray-900">O varejo merece tecnologia de verdade</h2>
          </div>
        </div>

        <div class="grid lg:grid-cols-[380px_1fr] gap-16 items-start"
          :class="whyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
          style="transition: opacity 700ms ease-out, transform 700ms ease-out">
          <blockquote class="border-l-4 border-kali-gold pl-6 py-1 lg:sticky lg:top-24">
            <p class="text-xl font-light text-gray-900 leading-relaxed">
              "O problema não era falta de tecnologia — era falta de tecnologia pensada para quem realmente precisa
              dela."
            </p>
            <cite class="mt-5 block text-sm text-gray-400 not-italic font-medium tracking-wide">Luísa, Kali
              Sistemas · 2020</cite>
          </blockquote>

          <div class="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              Todo semana, <strong class="text-gray-900 font-semibold">milhares de empresários perdem
                horas</strong>
              em planilhas desconectadas, fechamentos manuais e sistemas que não conversam entre si.
              Tempo que deveria estar no cliente, no produto, no crescimento.
            </p>
            <p>
              A Kali nasceu em 2020 para resolver isso. Não com mais um software complexo
              que exige semanas de treinamento — mas com um
              <strong class="text-gray-900 font-semibold">ecossistema completo que qualquer empresário consegue usar
                desde o primeiro dia</strong>.
            </p>
            <p>
              Começamos em Aracaju/SE com dois colaboradores. Hoje atendemos centenas de empresas
              em todos os estados do Brasil, cobrindo mais de 50 segmentos do varejo.
              Crescemos porque nossos clientes cresceram — e esse é o modelo.
            </p>
          </div>
        </div>

        <!-- Timeline -->
        <div class="mt-24" :class="whyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
          style="transition: opacity 700ms ease-out 300ms, transform 700ms ease-out 300ms">
          <div class="relative">
            <div class="absolute top-[10px] left-[10px] right-[10px] h-px bg-gray-300"></div>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              <div v-for="(m, i) in milestones" :key="m.year" class="relative pt-10">
                <div class="absolute top-0 w-5 h-5 rounded-full ring-4 ring-gray-50 shadow-sm"
                  :class="i === milestones.length - 1 ? 'bg-kali-gold' : 'bg-kali-blue-500'"></div>
                <span class="text-sm font-bold block mb-1"
                  :class="i === milestones.length - 1 ? 'text-amber-600' : 'text-kali-blue-600'">{{ m.year }}</span>
                <h4 class="font-bold text-gray-900 text-sm">{{ m.title }}</h4>
                <p class="text-xs text-gray-500 mt-1 leading-snug">{{ m.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 02 — NOSSAS SOLUÇÕES -->
    <section ref="solutionsRef" class="py-28 bg-white">
      <div class="container-custom">
        <div class="flex items-end gap-5 mb-6">
          <span class="text-[6rem] font-black text-gray-100 leading-none select-none" aria-hidden="true">02</span>
          <div class="pb-3">
            <p class="text-xs font-bold text-kali-blue-500 uppercase tracking-widest mb-1">O que construímos</p>
            <h2 class="text-3xl font-black text-gray-900">Soluções para cada segmento</h2>
          </div>
        </div>

        <p class="text-lg text-gray-600 max-w-2xl mb-14 leading-relaxed"
          :class="solutionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
          style="transition: opacity 700ms ease-out, transform 700ms ease-out">
          Cada negócio tem suas particularidades. Por isso desenvolvemos soluções
          especializadas — todas com a mesma base sólida, adaptadas à realidade de cada segmento.
        </p>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <RouterLink v-for="(sol, i) in solutions" :key="sol.name" :to="sol.to"
            class="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-kali-blue-200 hover:shadow-lg transition-all duration-300"
            :class="solutionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
            :style="`transition: opacity 600ms ease-out ${i * 80}ms, transform 600ms ease-out ${i * 80}ms, box-shadow 300ms, border-color 300ms`">
            <div class="aspect-[4/3] overflow-hidden bg-gray-50">
              <img :src="sol.image" :alt="sol.name"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy" />
            </div>
            <div class="p-5">
              <h3 class="font-bold text-gray-900 group-hover:text-kali-blue-600 transition-colors duration-200">{{
                sol.name }}</h3>
              <p class="text-sm text-gray-500 mt-1 leading-snug">{{ sol.description }}</p>
              <span
                class="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-kali-blue-500 group-hover:gap-2 transition-all duration-200">
                Saiba mais
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- 03 — PROPÓSITO + VALORES (único bloco dramático) -->
    <section ref="missionRef" class="py-28 bg-kali-blue-600 text-white">
      <div class="container-custom">
        <div class="flex items-end gap-5 mb-16">
          <span class="text-[6rem] font-black text-white/10 leading-none select-none" aria-hidden="true">03</span>
          <div class="pb-3">
            <p class="text-xs font-bold text-kali-gold uppercase tracking-widest mb-1">Quem somos</p>
            <h2 class="text-3xl font-black text-white">Propósito e valores</h2>
          </div>
        </div>

        <div class="grid lg:grid-cols-[3fr_2fr] gap-16 lg:gap-24 items-start">
          <!-- Propósito / Missão / Visão -->
          <div class="divide-y divide-white/20">
            <div v-for="(item, i) in pmvItems" :key="item.label" class="py-10"
              :class="missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
              :style="`transition: opacity 700ms ease-out ${i * 150}ms, transform 700ms ease-out ${i * 150}ms`">
              <p class="text-xs font-bold uppercase tracking-widest mb-4" :class="item.labelColor">{{ item.label }}</p>
              <p class="text-2xl sm:text-3xl font-light text-white leading-relaxed">{{ item.text }}</p>
            </div>
          </div>

          <!-- Valores -->
          <div :class="missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
            style="transition: opacity 700ms ease-out 300ms, transform 700ms ease-out 300ms">
            <p class="text-xs font-bold text-kali-blue-300 uppercase tracking-widest mb-6">Nossos valores</p>
            <div class="divide-y divide-white/20">
              <div v-for="(value, i) in values" :key="value.title" class="py-5"
                :class="missionVisible ? 'opacity-100' : 'opacity-0'"
                :style="`transition: opacity 500ms ease-out ${400 + i * 60}ms`">
                <p class="text-white font-semibold text-sm mb-1">{{ value.title }}</p>
                <p class="text-kali-blue-100/80 text-sm leading-relaxed">{{ value.line }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 04 — DIFERENCIAIS (no claro — respiro após bloco dark) -->
    <section ref="diffRef" class="py-28 bg-gray-50">
      <div class="container-custom">
        <div class="flex items-end gap-5 mb-16">
          <span class="text-[6rem] font-black text-gray-200 leading-none select-none" aria-hidden="true">04</span>
          <div class="pb-3">
            <p class="text-xs font-bold text-kali-blue-500 uppercase tracking-widest mb-1">Por que a Kali</p>
            <h2 class="text-3xl font-black text-gray-900">Não é discurso. É o que entregamos.</h2>
          </div>
        </div>

        <div class="grid md:grid-cols-2 gap-5">
          <div v-for="(diff, i) in differentials" :key="diff.title"
            class="group bg-white rounded-2xl border border-gray-100 p-8 hover:border-kali-blue-200 hover:shadow-md transition-all duration-300"
            :class="diffVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
            :style="`transition: opacity 600ms ease-out ${i * 80}ms, transform 600ms ease-out ${i * 80}ms, box-shadow 300ms, border-color 300ms`">
            <div class="flex items-start gap-5">
              <div
                class="w-11 h-11 rounded-xl bg-kali-blue-50 group-hover:bg-kali-blue-600 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                <svg class="w-5 h-5 text-kali-blue-600 group-hover:text-white transition-colors duration-300"
                  fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" :d="diff.icon" />
                </svg>
              </div>
              <div>
                <h3 class="text-base font-bold text-gray-900 mb-2">{{ diff.title }}</h3>
                <p class="text-gray-600 leading-relaxed text-sm">{{ diff.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CARREIRA -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <div class="grid lg:grid-cols-[1fr_420px] gap-12 lg:gap-20 items-center">
          <div class="relative order-2 lg:order-1 rounded-2xl overflow-hidden shadow-xl">
            <div
              class="absolute inset-0 bg-gradient-to-br from-kali-blue-600/10 to-transparent pointer-events-none z-10">
            </div>
            <img src="@/assets/careers.webp" alt="Equipe Kali Sistemas"
              class="w-full h-72 lg:h-96 object-cover object-center" loading="lazy" />
          </div>
          <div class="order-1 lg:order-2">
            <p class="text-xs font-bold text-kali-blue-500 uppercase tracking-widest mb-3">Trabalhe conosco</p>
            <h2 class="text-3xl font-black text-gray-900 sm:text-4xl leading-tight mb-5">
              Faça parte de um time que transforma o varejo brasileiro
            </h2>
            <p class="text-lg text-gray-600 leading-relaxed mb-8">
              Buscamos pessoas que gostam de resolver problemas reais, aprendem rápido
              e querem ver o impacto do seu trabalho no dia a dia de centenas de empresas.
            </p>
            <RouterLink to="/carreiras" class="inline-flex items-center gap-2 btn btn-primary">
              Ver oportunidades
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { RouterLink } from "vue-router";
import { useSeo } from "@/composables/useSeo";
import AboutHero from "@/components/about/Hero.vue";
import kaligestao from "@/assets/solutions/kaligestao.webp";
import kalifood from "@/assets/solutions/kalifood.webp";
import kaliclinica from "@/assets/solutions/kaliclinica.webp";
import kalisalao from "@/assets/solutions/kalisalao.webp";
import kalipet from "@/assets/solutions/kalipet.webp";
import kalishop from "@/assets/solutions/kalishop.webp";
import kaliagro from "@/assets/solutions/kaliagro.webp";
import kalistudio from "@/assets/solutions/kalistudio.webp";

defineOptions({ name: "AboutPage" });

useSeo({
  title: "Sobre Nós | Kali Sistemas",
  description: "Conheça a Kali Sistemas: nascemos para simplificar a gestão do varejo brasileiro. Tecnologia acessível para pequenos e médios negócios em todo o Brasil.",
});

// ─── Refs de seção ────────────────────────────────────────────────────────────
const statsRef = ref(null);
const whyRef = ref(null);
const solutionsRef = ref(null);
const missionRef = ref(null);
const diffRef = ref(null);

const statsVisible = ref(false);
const whyVisible = ref(false);
const solutionsVisible = ref(false);
const missionVisible = ref(false);
const diffVisible = ref(false);

let observers = [];

onMounted(() => {
  [
    { el: statsRef, vis: statsVisible, threshold: 0.2 },
    { el: whyRef, vis: whyVisible, threshold: 0.08 },
    { el: solutionsRef, vis: solutionsVisible, threshold: 0.06 },
    { el: missionRef, vis: missionVisible, threshold: 0.1 },
    { el: diffRef, vis: diffVisible, threshold: 0.08 },
  ].forEach(({ el, vis, threshold }) => {
    if (!el.value) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          vis.value = true;
          if (vis === statsVisible) startCounters();
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el.value);
    observers.push(obs);
  });
});

onBeforeUnmount(() => { observers.forEach((o) => o.disconnect()); observers = []; });

// ─── Stats counter ────────────────────────────────────────────────────────────
const statsItems = [
  { id: 1, target: 500, suffix: "+", prefix: "", label: "Clientes ativos" },
  { id: 2, target: 50, suffix: "+", prefix: "", label: "Segmentos atendidos" },
  { id: 3, target: 27, suffix: "", prefix: "", label: "Estados do Brasil" },
  { id: 4, target: 99, suffix: "%", prefix: "", label: "Satisfação nos atendimentos" },
];

const animatedStats = ref(statsItems.map(s => ({ ...s, displayed: 0 })));

function formatNumber(n) {
  try { return n.toLocaleString("pt-BR"); } catch { return String(n); }
}

function startCounters() {
  const duration = 1800;
  animatedStats.value.forEach((stat, idx) => {
    const delay = idx * 120;
    setTimeout(() => {
      const start = performance.now();
      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        stat.displayed = Math.floor(eased * stat.target);
        if (t < 1) requestAnimationFrame(tick);
        else stat.displayed = stat.target;
      };
      requestAnimationFrame(tick);
    }, delay);
  });
}

// ─── Dados ───────────────────────────────────────────────────────────────────
const milestones = [
  { year: "2020", title: "Fundação", description: "Nascemos em Aracaju/SE." },
  { year: "2021", title: "Gestão Web", description: "Lançamos o Kali Gestão." },
  { year: "2022", title: "Brasil inteiro", description: "Primeiros clientes nos 26 estados e no Distrito Federal." },
  { year: "2023", title: "100 clientes", description: "Superamos 100 empresas ativas na plataforma." },
  { year: "2024", title: "500 clientes", description: "+500 empresas gerindo seus negócios com a Kali." },
  { year: "Hoje", title: "+50 segmentos", description: "Ecossistema completo para o varejo nacional." },
];

const pmvItems = [
  {
    label: "Propósito",
    text: "Fazer o empresário brasileiro vender mais, perder menos tempo e dormir mais tranquilo.",
    labelColor: "text-kali-gold",
  },
  {
    label: "Missão",
    text: "Simplificar a gestão do varejo com tecnologia acessível, poderosa e fácil de usar. Impulsionando o crescimento dos nossos clientes.",
    labelColor: "text-kali-blue-300",
  },
  {
    label: "Visão",
    text: "Ser a referência nacional em software de gestão para o varejo, reconhecida pela inovação contínua e pela parceria verdadeira com cada cliente.",
    labelColor: "text-kali-blue-300",
  },
];

const values = [
  { title: "Inovação contínua", line: "Evoluímos o produto toda semana. O mercado não para — nós também não." },
  { title: "Simplicidade que funciona", line: "Se precisa de manual para usar, a gente refaz. Simples assim." },
  { title: "Parceria de verdade", line: "O sucesso do cliente é o nosso. Não é slogan — é o modelo do negócio." },
  { title: "Transparência total", line: "Sem letras miúdas, sem surpresas. Com clientes, parceiros e equipe." },
  { title: "Excelência em tudo", line: "O bom já foi. Buscamos o excepcional em cada detalhe." },
  { title: "O cliente no centro", line: "Conversamos com quem usa antes de qualquer funcionalidade." },
];

const solutions = [
  { name: "Kali Gestão", description: "ERP completo para o varejo geral", to: "/solucao/kali-gestao", image: kaligestao },
  { name: "Kali Food", description: "Bares, restaurantes e lanchonetes", to: "/solucao/kali-food", image: kalifood },
  { name: "Kali Clínica", description: "Clínicas médicas e odontológicas", to: "/solucao/kali-clinica", image: kaliclinica },
  { name: "Kali Salão", description: "Salões de beleza e barbearias", to: "/solucao/kali-salao", image: kalisalao },
  { name: "Kali Pet", description: "Pet shops e clínicas veterinárias", to: "/solucao/kali-pet", image: kalipet },
  { name: "Kali Shop", description: "E-commerce e lojas virtuais", to: "/solucao/kali-shop", image: kalishop },
  { name: "Kali Agro", description: "Gestão para o agronegócio", to: "/solucao/kali-agro", image: kaliagro },
  { name: "Kali Studio", description: "Estúdios de tatuagem e piercing", to: "/solucao/kali-studio", image: kalistudio },
];

const differentials = [
  {
    title: "Suporte humanizado — sem fila, sem bot",
    description: "Você fala com um especialista por chat ou WhatsApp. Nossa equipe responde em tempo real em dias úteis. Nenhum cliente fica sem resposta.",
    icon: "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z",
  },
  {
    title: "Funciona sem internet — você nunca para de vender",
    description: "A internet caiu? Sem problema. Nosso sistema híbrido garante operação contínua no PDV e no estoque. Os dados sincronizam automaticamente quando a conexão voltar.",
    icon: "M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0",
  },
  {
    title: "Implantação acompanhada — você não começa sozinho",
    description: "Nosso time configura o sistema, migra seus dados e treina sua equipe. Você opera com confiança desde o primeiro dia, sem perder vendas durante a transição.",
    icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  },
  {
    title: "Tudo em um lugar — sem gambiarras de integração",
    description: "PDV, Estoque, Financeiro, Fiscal, Loja Virtual e WhatsApp em um único sistema. Sem taxas escondidas de integração entre módulos, sem dados duplicados entre sistemas.",
    icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
  },
];
</script>
